import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Empty,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  theme,
} from 'antd';
import {
  BankOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { PageHeader } from 'components/headings';
import ComplianceBadge from 'components/admin/compliance/ComplianceBadge';
import EdificioDocumentActions from 'components/admin/Edificios/EdificioDocumentActions';
import EdificioEditorDrawer from 'components/admin/Edificios/EdificioEditorDrawer';
import { TIPO_USO_LABELS, useEdificios } from 'providers/Edificios';
import { useCliente } from 'providers/Clientes/ClienteProvider';
import { useRegulatoryAlerts } from 'providers/RegulatoryAlerts';
import useTenantFlags from 'hooks/useTenantFlags';
import useAuth from 'hooks/useAuth';
import { buildClienteTabPath } from 'utils/clienteNav';

const { Text } = Typography;
const { useToken } = theme;

// Tab "Edificios" de la ficha de cliente. Lista los edificios del cliente,
// permite crear/editar/eliminar, y desde cada fila se navega al tab "Equipos"
// con el filtro de edificio aplicado (via query param ?tab=ascensores&edificio=X).
// El compliance badge por edificio es placeholder hasta Fase 5; mientras tanto
// se computa en base a los equipos del edificio con certificaciones vigentes
// (aproximación — Fase 5 lo formaliza en el backend).
const EdificiosPage = ({ id }) => {
  const { token } = useToken();
  const { readOnly } = useTenantFlags();
  const { user } = useAuth();
  const isAdmin = user?.is_administrador;
  const navigate = useNavigate();
  const { edificios, loading, error, refresh, create, update, remove } = useEdificios(id);
  const { ascensores, callAscensores } = useCliente();
  const { alerts: clientAlerts } = useRegulatoryAlerts(id);

  // Si el usuario entró directo al tab "Edificios" sin pasar por "Equipos",
  // el ClienteProvider no disparó callAscensores todavía y la columna
  // "Equipos" mostraría 0 para todas las filas. Forzamos el fetch acá para
  // que el conteo sea correcto desde el primer render. useCliente cachea el
  // resultado, por lo que no hay doble request si el usuario después visita
  // el tab Equipos.
  useEffect(() => {
    if (id) callAscensores?.(id);
  }, [id, callAscensores]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  // Conteo de equipos por edificio desde los ascensores ya cargados por el
  // ClienteProvider. Evita una segunda request cuando el tab Equipos ya los
  // trajo; cuando no están cargados, el contador muestra 0 como fallback
  // aceptable (no bloquea la UI).
  const equiposPorEdificio = useMemo(() => {
    const acc = {};
    (ascensores ?? []).forEach((a) => {
      if (!a?.id_edificio) return;
      acc[a.id_edificio] = (acc[a.id_edificio] ?? 0) + 1;
    });
    return acc;
  }, [ascensores]);

  // Alertas agregadas por edificio. Consolida tipos y severidad para decidir
  // el status del ComplianceBadge: critical > warning > ok. Las alertas info
  // no escalan el status pero sí engrosan el tooltip.
  const alertsPorEdificio = useMemo(() => {
    const acc = new Map();
    for (const a of clientAlerts ?? []) {
      if (!a.id_edificio) continue;
      if (!acc.has(a.id_edificio)) {
        acc.set(a.id_edificio, { critical: 0, warning: 0, info: 0, total: 0, alerts: [] });
      }
      const bucket = acc.get(a.id_edificio);
      bucket[a.severidad] = (bucket[a.severidad] ?? 0) + 1;
      bucket.total += 1;
      bucket.alerts.push(a);
    }
    return acc;
  }, [clientAlerts]);

  const complianceFromBucket = (bucket) => {
    if (!bucket || bucket.total === 0) {
      return { status: 'ok', label: 'Al día', tooltip: 'Sin alertas regulatorias activas' };
    }
    const { critical, warning, info, total } = bucket;
    const tooltip = [
      critical > 0 ? `${critical} crítica${critical > 1 ? 's' : ''}` : null,
      warning > 0 ? `${warning} atención` : null,
      info > 0 ? `${info} info` : null,
    ]
      .filter(Boolean)
      .join(' · ');
    if (critical > 0) {
      return { status: 'critical', label: `${total} alertas`, tooltip };
    }
    if (warning > 0) {
      return { status: 'warning', label: `${total} alertas`, tooltip };
    }
    return { status: 'ok', label: `${info} info`, tooltip };
  };

  const openCreate = () => {
    setEditTarget(null);
    setDrawerOpen(true);
  };
  const openEdit = (row) => {
    setEditTarget(row);
    setDrawerOpen(true);
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditTarget(null);
  };
  const handleSubmit = async (payload) => {
    if (editTarget?.id) {
      await update(editTarget.id, payload);
    } else {
      await create(payload);
    }
    closeDrawer();
  };
  const handleDelete = async (row) => {
    try {
      await remove(row.id);
      message.success('Edificio eliminado');
    } catch (err) {
      const mensaje =
        err?.response?.status === 409
          ? 'No se puede eliminar: el edificio tiene equipos asociados'
          : err?.response?.data?.message || 'No se pudo eliminar';
      message.error(mensaje);
    }
  };

  const columns = [
    { title: 'Nombre', dataIndex: 'nombre', ellipsis: true, width: 220 },
    { title: 'Dirección', dataIndex: 'direccion', ellipsis: true },
    {
      title: 'Comuna',
      dataIndex: 'comuna',
      width: 140,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_uso',
      width: 120,
      render: (tipo) => <Tag>{TIPO_USO_LABELS[tipo] ?? tipo}</Tag>,
    },
    {
      title: 'Equipos',
      dataIndex: 'num_equipos',
      width: 100,
      render: (_, row) => (
        <Badge
          count={equiposPorEdificio[row.id] ?? 0}
          showZero
          style={{ backgroundColor: token.colorPrimary }}
        />
      ),
    },
    {
      title: 'Recepción DOM',
      dataIndex: 'fecha_recepcion_dom',
      width: 140,
      render: (d) => (d ? dayjs(d).format('DD/MM/YYYY') : <Text type="secondary">—</Text>),
    },
    {
      title: 'Cumplimiento',
      dataIndex: 'cert_status',
      width: 160,
      render: (_, row) => {
        const { status, label, tooltip } = complianceFromBucket(alertsPorEdificio.get(row.id));
        return <ComplianceBadge status={status} label={label} tooltip={tooltip} />;
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 200,
      render: (_, row) => (
        <Space size="small">
          <Tooltip title="Ver equipos del edificio">
            <Button
              icon={<RightOutlined />}
              size="small"
              type="primary"
              onClick={() =>
                navigate(buildClienteTabPath(isAdmin, id, 'ascensores', { edificio: row.id }))
              }
            />
          </Tooltip>
          {!readOnly && (
            <Tooltip title="Editar">
              <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(row)} />
            </Tooltip>
          )}
          <EdificioDocumentActions edificio={row} />
          {!readOnly && (
            <Popconfirm
              title="Eliminar edificio"
              description={`¿Eliminar "${row.nombre}"? Sólo funciona si no tiene equipos.`}
              okText="Eliminar"
              okType="danger"
              cancelText="Cancelar"
              onConfirm={() => handleDelete(row)}
            >
              <Tooltip title="Eliminar">
                <Button icon={<DeleteOutlined />} size="small" danger />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Filtros client-side: buscador (nombre/dirección) + comuna (valores únicos).
  const [search, setSearch] = useState('');
  const [comuna, setComuna] = useState(null);
  const comunaOptions = useMemo(() => {
    const set = new Set((edificios ?? []).map((e) => e.comuna).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b)).map((c) => ({ value: c, label: c }));
  }, [edificios]);

  const filteredEdificios = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (edificios ?? []).filter((e) => {
      const matchQ = !q || [e.nombre, e.direccion].some((v) => (v || '').toLowerCase().includes(q));
      const matchComuna = !comuna || e.comuna === comuna;
      return matchQ && matchComuna;
    });
  }, [edificios, search, comuna]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginLG,
        paddingTop: token.paddingMD,
        paddingBottom: token.paddingXL,
      }}
    >
      <PageHeader
        icon={BankOutlined}
        title="Edificios"
        subtitle="Expediente regulatorio MINVU por edificio"
        actions={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
              Actualizar
            </Button>
            {!readOnly && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                Nuevo edificio
              </Button>
            )}
          </Space>
        }
      />

      <Space wrap>
        <Input.Search
          allowClear
          placeholder="Buscar por nombre o dirección…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          showSearch
          placeholder="Comuna"
          value={comuna}
          onChange={setComuna}
          options={comunaOptions}
          optionFilterProp="label"
          style={{ width: 200 }}
        />
      </Space>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los edificios"
          description="Revisa tu conexión y reintenta. Si persiste, avisa al equipo."
          action={
            <Button type="primary" onClick={refresh}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filteredEdificios}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          locale={{
            emptyText: (
              <Empty description="Todavía no hay edificios registrados para este cliente." />
            ),
          }}
        />
      )}

      <EdificioEditorDrawer
        open={drawerOpen}
        edificio={editTarget}
        onClose={closeDrawer}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default EdificiosPage;
